import { ReactNode, useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { tokens } from '@fhs/ui';
import { Nullable } from '@rbi-ctg/frontend';

import { PointsBadge } from '../points-badge';

export interface VerticalTier {
  id: string;
  label: string;
  content: ReactNode;
  enabled?: Nullable<boolean>;
}

interface Props {
  tiers: VerticalTier[];
  style?: StyleProp<ViewStyle>;
}

export function VerticalTieredProgressBar({ tiers, style }: Props) {
  const [initialTiersWidths, setInitialTiersWidths] = useState<number[]>([]);
  const readyToRenderContent = initialTiersWidths.length === tiers.length;
  const biggestTierWidth = useMemo(() => {
    return readyToRenderContent
      ? initialTiersWidths.reduce((acc, curr) => {
          return Math.max(acc, curr);
        }, 0)
      : undefined;
  }, [initialTiersWidths, readyToRenderContent]);

  const onLayout = useCallback((e: LayoutChangeEvent, index: number) => {
    const newWidth = Math.ceil(e.nativeEvent.layout.width) + 2;
    setInitialTiersWidths(prev => {
      const newWidths = [...prev];
      if (!newWidths[index]) {
        newWidths[index] = newWidth;
      }
      return newWidths;
    });
  }, []);

  return (
    <View style={style}>
      {tiers.map((tier, index) => (
        <View key={tier.id} style={styles.tierContainer}>
          <View
            onLayout={e => onLayout(e, index)}
            style={[styles.tier, { width: biggestTierWidth || 'auto' }]}
          >
            <PointsBadge enabled={tier.enabled} label={tier.label} />
            {tiers.length - 1 !== index && <View style={styles.tierSeparator} />}
          </View>
          <View style={styles.contentContainer}>{readyToRenderContent && tier.content}</View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  tierContainer: {
    flexDirection: 'row',
  },
  tier: {
    marginRight: 12,
  },
  tierSeparator: {
    flex: 1,
    width: 1,
    alignSelf: 'center',
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  contentContainer: {
    flex: 1,
    marginBottom: 24,
    minHeight: 70,
  },
});
